import rp, { createScheduleControls, encodeSchedule } from '@reporting/rp-app-host';
import { Form, FormAdapter, FormAlert, FormContainerOptions, FormControlProperties, FormControlType, FormValues } from '@omegagrid/form';
import { msg } from "@omegagrid/localize";
import { Task } from '@reporting/rp-types/deployment';

/** The script types the rp-tasks poller runs (packages/rp-tasks/exec.ps1). */
const SCRIPT_TYPES = [
	{ k: 'powershell', v: 'PowerShell' },
];

/**
 * The task form: name, script and target of a DPL_TASK row plus the shared schedule controls
 * (the same ones as the report e-mail form, with the sub-day repetition only the tasks have).
 * Saves the whole task through POST /tasks.
 */
export class TaskFormAdapter extends FormAdapter {

	private frequencyType: number;

	/** the FREQ_SUBDAY_TYPE chosen in the form (1 = once a day, as the stored null reads) */
	private subdayType: number;

	/** @param task the edited task; null for a new one (a copy comes without id_task) */
	constructor(
		private task: Task = null,
	) {
		super();
		this.frequencyType = this.task?.freq_type || 1;
		this.subdayType = this.task?.freq_subday_type || 1;
	}

	// only these travel to the api: last_run / next_run belong to the poller and the schedule function
	private formFields = ['name', 'enabled', 'script', 'script_type', 'target_server'] as const;

	getColumns() {
		return [
			{ width: 150 },
			{ width: null },
			{ width: 85 },
		];
	}

	async getControls(): Promise<FormControlProperties[]> {
		let r = 0;
		const controls: FormControlProperties[] = [
			{row: r, col: 0, type: FormControlType.Label, value: msg('Name'), for: 'name'},
			{row: r++, col: 1, cs: 2, type: FormControlType.TextInput, key: 'name', value: this.task?.name || null, required: true},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Enabled'), for: 'enabled'},
			{row: r++, col: 1, cs: 2, type: FormControlType.Checkbox, key: 'enabled', value: this.task ? !!this.task.enabled : true},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Script'), for: 'script'},
			{row: r++, col: 1, cs: 2, type: FormControlType.TextInput, key: 'script', value: this.task?.script || null, required: true},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Script type'), for: 'script_type'},
			{row: r++, col: 1, cs: 2, type: FormControlType.List, key: 'script_type', value: this.task?.script_type || SCRIPT_TYPES[0].k, content: {
				sourceData: SCRIPT_TYPES,
				options: { selectMode: 'single', autoClose: true }
			}},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Target server'), for: 'target_server'},
			{row: r++, col: 1, cs: 2, type: FormControlType.TextInput, key: 'target_server', value: this.task?.target_server || null},
		];

		const schedule = createScheduleControls(this.task, this.frequencyType, r, 2, { type: this.subdayType });
		controls.push(...schedule.controls);
		return controls;
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: ["save", "close"]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		const task: Task = {
			...Object.fromEntries(this.formFields.map(key => [key, formValues[key]])),
			// a one-off has no sub-day controls: clear a repetition the task had before
			freq_subday_type: null,
			freq_subday_interval: null,
			...encodeSchedule(formValues),
			id_task: this.task?.id_task ?? null,
		};
		await rp.ms('deployment').post('tasks', task);
		return true;
	}

	updateValues = (controls: FormControlProperties[], values: FormValues) => {
		if (!values) return controls;
		const map = new Map(controls.filter(item => item.key).map(item => [item.key, item]));
		Object.entries(values).forEach(([k, v]) => Object.assign(map.get(k) || {}, { value: v }));
		return controls;
	}

	/** Re-creates the controls when the frequency or the sub-day type changes, so their interval controls appear. */
	async updateFormControls(form: Form) {
		const freqType = form.values.freq_type as number;
		const subdayType = (form.values.freq_subday_type as number) ?? this.subdayType;
		if (freqType !== this.frequencyType || subdayType !== this.subdayType) {
			this.frequencyType = freqType;
			this.subdayType = subdayType;
			form.setSourceData({
				controls: this.updateValues(await this.getControls(), form.values),
				columns: this.getColumns()
			});
		}
	}

}
